import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { ReactNode } from "react";
import GfgModal from "@/components/GfgModal";
import { COPY } from "@/constants/sendQueueCampaigns";
import type { SendQueueItem } from "@/interfaces/sendQueueCampaigns";
import { reminderLabel } from "@/utils/sendQueueCampaigns-helper";

/**
 * Every Send Queue confirm-before-acting GfgModal, driven by one config map — the
 * CampaignBuilderModal pattern, as reused by PlanActionModal. A new confirm is a new key in
 * `SendQueueConfirmContext` plus its entry below; no new component, and the copy stays in
 * constants/sendQueueCampaigns.ts.
 *
 * Both current contexts guard an irreversible action: "Send now" delivers the gifts immediately
 * and "Cancel send" drops the send outright, so neither can be undone once confirmed.
 *
 * The panel mounts this only while its context is the open one
 * (`dialog === "sendNow" && activeItem && <ConfirmActionModal .../>`), so closing unmounts it
 * outright instead of toggling it hidden — that is what stops a second click landing on a live
 * Confirm button during MUI's exit transition. `onDismiss` is deliberately left unwired, so
 * dismissing by backdrop or Escape only closes and never confirms.
 */
export type SendQueueConfirmContext = "sendNow" | "cancel";

type ConfirmConfig = {
  icon: ReactNode;
  title: (item: SendQueueItem) => string;
  description: (item: SendQueueItem) => ReactNode;
  primaryLabel: string;
  /** "danger" paints the primary button wine — destructive contexts only. */
  primaryVariant?: "primary" | "danger";
};

const CONFIGS: Record<SendQueueConfirmContext, ConfirmConfig> = {
  sendNow: {
    icon: <SendOutlinedIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: (item) => COPY.sendNowTitle(item.recipients),
    description: (item) => `${reminderLabel(item)} · ${item.campaignTitle}`,
    primaryLabel: COPY.rowMenu.sendNow,
  },
  cancel: {
    icon: <WarningAmberIcon sx={{ fontSize: 28, color: "var(--color-dark-dust)" }} />,
    title: () => COPY.cancel.title,
    description: (item) => COPY.cancel.body(item.recipients),
    primaryLabel: COPY.rowMenu.cancelSend,
    primaryVariant: "danger",
  },
};

export interface ConfirmActionModalProps {
  /** Picks which confirm renders. The panel mounts this component only when one is open. */
  context: SendQueueConfirmContext;
  /** The row being acted on — every config reads its recipients and campaign from here. */
  item: SendQueueItem;
  onClose: () => void;
  /** Confirms the action. The modal never calls the API — the caller owns the mutation. */
  onConfirm: () => void;
}

export default function ConfirmActionModal({
  context,
  item,
  onClose,
  onConfirm,
}: ConfirmActionModalProps) {
  const config = CONFIGS[context];

  return (
    <GfgModal
      open
      onClose={onClose}
      onPrimary={onConfirm}
      primaryVariant={config.primaryVariant}
      icon={config.icon}
      title={config.title(item)}
      description={config.description(item)}
      primaryLabel={config.primaryLabel}
      secondaryLabel={COPY.modal.cancel}
    />
  );
}
